import { Injectable } from '@angular/core';
import type { ProviderType } from '../../../models/project-integration.model';
import type { AcceptanceCriterionDraft, CoverageLinkDraft } from '../../../models/save-full-requirement.model';
import type {
  AutomationStatus,
  RequirementPriority,
  RequirementStatus,
  RequirementStatusOption,
  RequirementType,
} from '../../../models/requirement.model';
import type { KiEntwurfVorgemerkt } from '../../../models/ki.model';
import type { StatusEditorEntry } from '../requirement-status-editor/requirement-status-editor.component';

const DRAFT_KEY = 'om-anforderung-draft';
const NEW_TC_KEY = 'om-anforderung-new-tc';

/** Platzhalter-ID der noch nicht angelegten Anforderung in Entwurf und Rückweg (`anfId=neu`). */
export const NEW_REQUIREMENT_DRAFT_ID = 'neu';

export interface RequirementFormDraftFormState {
  title: string;
  description: string;
  type: RequirementType;
  priority: RequirementPriority;
  status: RequirementStatus;
  ownerId: string | null;
  reviewerIds: string[];
  version: string;
  tags: string[];
  jiraIssueKey: string;
  notes: string;
  parentRequirementId: string;
  automationStatus: AutomationStatus;
  testFramework: string | null;
}

export interface RequirementFormDraft {
  requirementId: string;
  projectId?: string;
  pickedSummary?: string | null;
  form: RequirementFormDraftFormState;
  acDrafts: AcceptanceCriterionDraft[];
  coverageDrafts: CoverageLinkDraft[];
  loadedReviewerIds: string[];
  providerUnlinked: boolean;
  selectedProviderType: ProviderType | null;
  statusEditorEntries: StatusEditorEntry[];
  baselineStatusValues: string[];
  statusOptions?: RequirementStatusOption[];
  pendingMigrations: Record<string, string>;
  criteriaLoadFailed?: boolean;
  coverageLoadFailed?: boolean;
  lockVersion?: number | null;
  /** Nach einem Teilfehler beim Anlegen schon gespeichert; das nächste Speichern geht per PUT an sie. */
  savedRequirementId?: string | null;
  kiVorgemerkt?: KiEntwurfVorgemerkt[];
}

interface NewTestCaseRecord {
  requirementId: string;
  testCaseId: string;
  projectId?: string;
}

// Der Entwurf „neu“ gilt nur im Projekt, in dem er entstand; ohne Projekt im Eintrag ist er keinem
// zuzuordnen. Einträge zu gespeicherten Anforderungen von vor dem Deploy tragen kein Projekt und gelten weiter.
function gehoertZu(
  entry: { requirementId: string; projectId?: string },
  requirementId: string,
  projectId?: string,
): boolean {
  if (entry.requirementId !== requirementId) return false;
  if (entry.projectId === undefined) return requirementId !== NEW_REQUIREMENT_DRAFT_ID;
  return projectId === undefined || entry.projectId === projectId;
}

@Injectable({ providedIn: 'root' })
export class RequirementFormDraftStore {
  snapshot(draft: RequirementFormDraft): void {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, savedAt: new Date().toISOString() }));
  }

  restore(requirementId: string, projectId?: string): RequirementFormDraft | null {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as RequirementFormDraft;
      return gehoertZu(parsed, requirementId, projectId) ? parsed : null;
    } catch {
      sessionStorage.removeItem(DRAFT_KEY);
      return null;
    }
  }

  clear(): void {
    sessionStorage.removeItem(DRAFT_KEY);
  }

  rememberCreatedTestCaseId(requirementId: string, testCaseId: string, projectId?: string): void {
    const record: NewTestCaseRecord = { requirementId, testCaseId, projectId };
    sessionStorage.setItem(NEW_TC_KEY, JSON.stringify(record));
  }

  consumeCreatedTestCaseId(requirementId: string, projectId?: string): string | null {
    const raw = sessionStorage.getItem(NEW_TC_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(NEW_TC_KEY);
    try {
      const parsed = JSON.parse(raw) as NewTestCaseRecord;
      return gehoertZu(parsed, requirementId, projectId) ? parsed.testCaseId : null;
    } catch {
      return null;
    }
  }
}
